import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { ConfirmPanel } from './ConfirmDialog';

/** The discard question's own words, for an editor whose loss is not "your changes". */
export interface DiscardCopy {
  title: string;
  consequence: string;
  confirmLabel: string;
}

interface DialogGuardBodyProps {
  guard: DialogDirtyGuard;
  /** Replaces the generic "Discard your changes?" (the top-up's "Discard this top-up?"). */
  copy?: DiscardCopy | undefined;
  /**
   * The question heading's id, when the host names itself by a fixed id (the top-up in a modal
   * host, whose `aria-labelledby` is the swap's title id); the guard's own otherwise.
   */
  titleId?: string | undefined;
  /** The editor's content (title, fields, actions). */
  children: ReactNode;
}

/**
 * The content of a container that guards unsaved work (TMMAPP-0073): the editor, and — when the
 * guard is asking — the discard question in its place. The editor is hidden, not unmounted
 * (`display: none` takes it out of the tab order and the accessibility tree), so "Keep editing"
 * returns every value, including state held by the editor's own children. `display: contents`
 * keeps its layout against the host identical when it is showing.
 *
 * The host (a `Dialog`, or a `Drawer` — a modal `role="dialog"` too) names itself by
 * `guard.titleId` while the question shows (`GuardedDialog` does it for a dialog).
 */
export function DialogGuardBody({ guard, copy, titleId, children }: DialogGuardBodyProps) {
  const { t } = useTranslation();
  const { attachBody, discarding } = guard;
  return (
    <>
      {discarding && (
        <ConfirmPanel
          title={copy?.title ?? t('unsaved.title')}
          consequence={copy?.consequence ?? t('unsaved.consequence')}
          confirmLabel={copy?.confirmLabel ?? t('unsaved.discard')}
          cancelLabel={t('unsaved.keep')}
          destructive
          titleId={titleId ?? guard.titleId}
          consequenceId={guard.consequenceId}
          focusHeading
          onConfirm={guard.discard}
          onCancel={guard.keepEditing}
        />
      )}
      <div ref={attachBody} style={{ display: discarding ? 'none' : 'contents' }}>
        {children}
      </div>
    </>
  );
}
